'use client'

import { useState, useEffect, useRef } from 'react'
import { REACTION_EMOJIS } from '@/types'
import type { DemoReaction } from '../demo-types'

interface ReactionEntry {
  id: string
  emoji: string
}

export interface ReactionBubblesProps {
  reactions: DemoReaction[]
}

/**
 * Sticky reaction ticker — shows recent reactions as a horizontal stream
 * of emojis that stays visible regardless of scroll position.
 */
export function ReactionBubbles({ reactions }: ReactionBubblesProps) {
  const [visible, setVisible] = useState<ReactionEntry[]>([])
  const processedCountRef = useRef(0)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (reactions.length > processedCountRef.current) {
      const newReactions = reactions.slice(processedCountRef.current)
      const entries = newReactions.map(r => ({
        id: r.id,
        emoji: REACTION_EMOJIS[r.type],
      }))

      setVisible(prev => [...prev, ...entries].slice(-12))

      // Auto-hide after inactivity
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
      hideTimerRef.current = setTimeout(() => {
        setVisible([])
      }, 5000)

      processedCountRef.current = reactions.length
    }
  }, [reactions])

  // Cleanup
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    }
  }, [])

  if (visible.length === 0) return null

  return (
    <div className="sticky top-0 z-40 pointer-events-none">
      <div className="flex items-center gap-0.5 px-3 py-1.5 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-pink-500/10 border-b border-pink-500/20 overflow-hidden">
        <div className="flex items-center gap-1 animate-in slide-in-from-right duration-300">
          {visible.map((entry) => (
            <span
              key={entry.id}
              className="text-lg animate-in zoom-in duration-200"
              style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.2))' }}
            >
              {entry.emoji}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

// No longer needed but keep export for backward compatibility
export function ReactionBubblesStyles() {
  return null
}
